import { memo, useCallback, useContext, useRef } from 'react';
import { getGelTokens } from "@tal-gel/theming";
import {
    GelRowLayout,
    GelLabel,
    GelParagraph,
    GelContainerLite,
    GelCollapsible,
    GelBoxLayout,
    GelIcon
} from '@tal-gel/components';
import Uploader from '../documentsupload/uploader';
import { UserContext } from '../../../common/usercontext/user.context';
import { LMC, NotificationItemIds } from '../../../constants/constants';
import { CLAIM_ASSESSMENT_STATUS } from '../../../constants/constants';
import { useGetClaimDetailsContent } from '../../../common/api/graphQLDataFetch';
import { activeUploadActions, useActiveUploadStore } from '../documentsupload/activeUploadStore';

interface OtherDocumentsProps {
    claimId: number;
    claimAssesmentStatus: string;
}

const OtherDocuments = ({ claimId, claimAssesmentStatus }: OtherDocumentsProps) => {
    const { global: { fontSizeBodyLarge, sizeBaseUnit, themeColorBorderDefault, themeColorBackgroundSuccess, fontFamilySans, fontWeightRegular } } = getGelTokens();
    const { contextData } = useContext(UserContext);
    const { data } = useGetClaimDetailsContent()    
    const activeId = useActiveUploadStore(s => s.activeRequirementId);
    
    const isOpen = activeId === NotificationItemIds.OtherDocuments;

    const allowCollapseRef = useRef(true);
    const onDisableCollapse = useCallback((allowCollapse: boolean) => {
        allowCollapseRef.current = allowCollapse;
    }, []);

    const onHeaderClick = useCallback(() => {
        if (!allowCollapseRef.current) return;

        const opening = !isOpen;
        activeUploadActions.setActiveRequirement(opening ? (NotificationItemIds.OtherDocuments ?? null) : null);

        activeUploadActions.consumeUploadComplete();

    }, [isOpen]);

    return (
        <div id={NotificationItemIds.OtherDocuments}>
            <GelRowLayout style={{ padding: sizeBaseUnit * 4 }}>
                <GelContainerLite style={{
                    borderTop: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                    borderLeft: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                    borderRight: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                    paddingTop: sizeBaseUnit,
                    paddingBottom: isOpen ? sizeBaseUnit * 2 : 0,
                    width: "auto",
                    cursor: "pointer",
                }}
                    onClick={onHeaderClick}>
                    <GelBoxLayout space={[7, 0.1]} style={{
                        borderBottom: isOpen ? `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}` : "none",
                        width: "auto",
                        padding: sizeBaseUnit * 3,
                        marginLeft: -sizeBaseUnit * 4,
                        marginRight: -sizeBaseUnit * 4
                    }}>
                        <GelLabel
                            style={{
                                fontFamily: fontFamilySans,
                                fontSize: sizeBaseUnit * 4,
                                fontWeight: fontWeightRegular
                            }}>
                            {data?.additionalDocuments?.OtherDocumentsAccordionTitle}
                        </GelLabel>

                        <GelIcon
                            name={isOpen ? "ChevronUp" : "ChevronDown"}
                            width={sizeBaseUnit * 6}
                            color={themeColorBackgroundSuccess}>
                        </GelIcon>
                    </GelBoxLayout>
                </GelContainerLite>

                <GelContainerLite style={{
                    borderBottom: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                    borderLeft: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                    borderRight: `${sizeBaseUnit / 4}px solid ${themeColorBorderDefault}`,
                    paddingBottom: sizeBaseUnit * 2,
                    marginTop: -sizeBaseUnit * 8,
                }} >
                    <GelCollapsible open={isOpen}>
                        <GelContainerLite style={{ paddingBottom: sizeBaseUnit * 2 }}>
                            <GelRowLayout gutter="xsmall" style={{                                
                                paddingBottom: sizeBaseUnit,
                            }} data-upload-root="true">
                                <GelParagraph style={{ fontWeight: '400px', fontSize: fontSizeBodyLarge, paddingTop: sizeBaseUnit * 4, paddingBottom: sizeBaseUnit * 2 }}>
                                    {claimAssesmentStatus === CLAIM_ASSESSMENT_STATUS.RN ? data?.documentClaimNotifiedText
                                        : data?.otherDocumentText}
                                </GelParagraph>
                            </GelRowLayout>

                            <Uploader
                                requirement={{
                                    claimId: claimId,
                                    associatedDocuments: contextData?.otherDocuments?.filter((doc) => doc.claimId === claimId && doc.isAssociatedRequirementFound === false
                                        && !doc.createdBy.toLowerCase().includes(LMC.toLowerCase())),
                                }}
                                setAllowCollapse={onDisableCollapse}
                            />
                        </GelContainerLite>
                    </GelCollapsible>
                </GelContainerLite>
            </GelRowLayout>
        </div>
    );
};

export default memo(OtherDocuments);